import { css } from 'styled-components';
import { SDS_SPACE_XXXSMALL, SDS_SPACE_XXXXSMALL, SDS_SPACE_XXSMALL } from '@s-group/design-system-tokens/web/tokens/space.es6.js';
import { SDS_FONT_WEIGHT_MEDIUM, SDS_FONT_FAMILY_PRIMARY, SDS_FONT_SIZE_BODY_XSMALL, SDS_FONT_LINEHEIGHT_BODY_XSMALL, SDS_FONT_SIZE_BODY_SMALL, SDS_FONT_LINEHEIGHT_BODY_SMALL, SDS_FONT_SIZE_HEADING_XXSMALL, SDS_FONT_LINEHEIGHT_HEADING_XXSMALL, SDS_FONT_SIZE_HEADING_XSMALL, SDS_FONT_LINEHEIGHT_HEADING_XSMALL } from '@s-group/design-system-tokens/web/tokens/font.es6.js';
import { SDS_SIZE_BORDER_RADIUS_NONE, SDS_SIZE_BORDER_RADIUS_XXXSMALL, SDS_SIZE_BORDER_RADIUS_XSMALL, SDS_SIZE_BORDER_RADIUS_SMALL, SDS_SIZE_BORDER_RADIUS_MEDIUM, SDS_SIZE_BORDER_RADIUS_LARGE, SDS_SIZE_BORDER_RADIUS_XLARGE, SDS_SIZE_BORDER_SMALL } from '@s-group/design-system-tokens/web/tokens/size.es6.js';
import { math } from 'polished';
import { SDS } from '../common/namespace.js';

const getFixedComponentDimensions = ({ fixed, sizing }) => {
    if (!fixed || !sizing) {
        return;
    }
    const [, lineHeight] = fontMetrics[sizing];
    const [verticalPadding] = paddingMetrics[sizing];
    return math(`2* ${verticalPadding} + ${lineHeight}`);
};
const backgroundColor = ({ color, theme, variant, }) => variant === 'filled'
    ? color === 'primary'
        ? theme[SDS].colors.SDS_BRAND_COLOR_BACKGROUND_STRONG_PRIMARY
        : color === 'neutral'
            ? theme[SDS].colors.SDS_BRAND_COLOR_BACKGROUND_INVERSE_NEUTRAL
            : undefined
    : variant === 'tonal'
        ? color === 'primary'
            ? theme[SDS].colors.SDS_BRAND_COLOR_BACKGROUND_WEAK_PRIMARY
            : color === 'neutral'
                ? theme[SDS].colors.SDS_BRAND_COLOR_BACKGROUND_WEAK_NEUTRAL
                : color === 'info'
                    ? theme[SDS].colors.SDS_BRAND_COLOR_BACKGROUND_INFO
                    : color === 'success'
                        ? theme[SDS].colors.SDS_BRAND_COLOR_BACKGROUND_SUCCESS
                        : color === 'warning'
                            ? theme[SDS].colors.SDS_BRAND_COLOR_BACKGROUND_WARNING
                            : color === 'error'
                                ? theme[SDS].colors.SDS_BRAND_COLOR_BACKGROUND_ERROR
                                : undefined
        : variant === 'outlined'
            ? 'transparent'
            : undefined;
const borderColor = ({ color, theme, variant }) => variant === 'outlined'
    ? color === 'primary'
        ? theme[SDS].colors.SDS_BRAND_COLOR_BORDER_STRONG_PRIMARY
        : color === 'neutral'
            ? theme[SDS].colors.SDS_BRAND_COLOR_BORDER_DEFAULT_NEUTRAL
            : color === 'info'
                ? theme[SDS].colors.SDS_BRAND_COLOR_BORDER_INFO
                : color === 'success'
                    ? theme[SDS].colors.SDS_BRAND_COLOR_BORDER_SUCCESS
                    : color === 'warning'
                        ? theme[SDS].colors.SDS_BRAND_COLOR_BORDER_WARNING
                        : color === 'error'
                            ? theme[SDS].colors.SDS_BRAND_COLOR_BORDER_ERROR
                            : undefined
    : undefined;
const borderRadius = ({ rounding, sizing }) => rounding && rounding === 'sharp'
    ? SDS_SIZE_BORDER_RADIUS_NONE
    : rounding === 'rounded'
        ? SDS_SIZE_BORDER_RADIUS_XXXSMALL
        : sizing &&
            {
                // sizing: border-radius
                xsmall: SDS_SIZE_BORDER_RADIUS_XSMALL,
                small: SDS_SIZE_BORDER_RADIUS_SMALL,
                medium: SDS_SIZE_BORDER_RADIUS_MEDIUM,
                large: SDS_SIZE_BORDER_RADIUS_LARGE,
                xlarge: SDS_SIZE_BORDER_RADIUS_XLARGE,
            }[sizing];
const borderWidth = ({ variant }) => variant === 'outlined' ? SDS_SIZE_BORDER_SMALL : 0;
const fontMetrics = {
    // sizing: [font-size, line-height]
    xsmall: [SDS_FONT_SIZE_BODY_XSMALL, SDS_FONT_LINEHEIGHT_BODY_XSMALL],
    small: [SDS_FONT_SIZE_BODY_XSMALL, SDS_FONT_LINEHEIGHT_BODY_XSMALL],
    medium: [SDS_FONT_SIZE_BODY_SMALL, SDS_FONT_LINEHEIGHT_BODY_SMALL],
    large: [SDS_FONT_SIZE_HEADING_XXSMALL, SDS_FONT_LINEHEIGHT_HEADING_XXSMALL],
    xlarge: [SDS_FONT_SIZE_HEADING_XSMALL, SDS_FONT_LINEHEIGHT_HEADING_XSMALL],
};
const font = ({ sizing }) => sizing &&
    [fontMetrics[sizing]]
        .map(([fontSize, lineHeight]) => `${SDS_FONT_WEIGHT_MEDIUM} ${fontSize} / ${lineHeight} '${SDS_FONT_FAMILY_PRIMARY}'`)
        .join('');
const paddingMetrics = {
    // size: [vertical, horizontal]
    xsmall: ['0', SDS_SPACE_XXXSMALL],
    small: [SDS_SPACE_XXXXSMALL, SDS_SPACE_XXXSMALL],
    medium: [SDS_SPACE_XXXXSMALL, SDS_SPACE_XXSMALL],
    large: [SDS_SPACE_XXXSMALL, SDS_SPACE_XXSMALL],
    xlarge: [SDS_SPACE_XXXSMALL, SDS_SPACE_XXSMALL],
};
const padding = ({ fixed, sizing }) => !fixed &&
    sizing &&
    paddingMetrics[sizing].map(dimension => dimension).join(' ');
const textColor = ({ color, theme, variant }) => variant === 'filled'
    ? theme[SDS].colors.SDS_BRAND_COLOR_TEXT_INVERSE_NEUTRAL
    : variant === 'tonal' || variant === 'outlined'
        ? color === 'primary'
            ? theme[SDS].colors.SDS_BRAND_COLOR_TEXT_STRONG_PRIMARY
            : color === 'neutral'
                ? theme[SDS].colors.SDS_BRAND_COLOR_TEXT_DEFAULT_NEUTRAL
                : color === 'info'
                    ? theme[SDS].colors.SDS_BRAND_COLOR_TEXT_INFO
                    : color === 'success'
                        ? theme[SDS].colors.SDS_BRAND_COLOR_TEXT_SUCCESS
                        : color === 'warning'
                            ? theme[SDS].colors.SDS_BRAND_COLOR_TEXT_WARNING
                            : color === 'error'
                                ? theme[SDS].colors.SDS_BRAND_COLOR_TEXT_ERROR
                                : undefined
        : undefined;
const TextBadgeStyle = css `
    align-items: center;
    background-color: ${backgroundColor};
    border: ${borderWidth} solid ${borderColor};
    border-radius: ${borderRadius};
    color: ${textColor};
    display: inline-flex;
    font: ${font};
    height: ${getFixedComponentDimensions};
    justify-content: center;
    padding: ${padding};
    text-align: center;
    width: ${getFixedComponentDimensions};
`;

export { TextBadgeStyle };
//# sourceMappingURL=TextBadge.styles.js.map
